import { useState } from 'react'
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

/** The `title="..."` a code fence's meta string carries, if any — the same
 * rule the renderer uses, so the tab strip here reads like the published page. */
function metaTitle(meta: string | null | undefined): string | null {
  const match = meta ? /(?:^|\s)title="([^"]*)"/.exec(meta) : null
  return match?.[1]?.trim() || null
}

/**
 * A set of fenced code blocks presented as tabs, one per language — cURL,
 * JavaScript, Python and so on for the same request.
 *
 * Unlike `tabs`, there is no wrapping node per panel: a fenced code block
 * already names itself (its language, or a `title=` in its meta string), so
 * wrapping each one in a titled container would just be somewhere else for
 * that name to drift out of sync.
 *
 * The strip below only NAVIGATES — clicking a label moves the caret into that
 * panel, it does not hide the others. `tabs` can hide inactive panels because
 * `tab` is a node this editor gives its own view, which can carry a
 * `data-index` attribute for the CSS in editor.css to match against. A
 * fenced code block has no such wrapper here — it is plain ProseMirror
 * content, not a React node view — so there is nowhere to hang that
 * attribute without giving every code block in the editor a view it does not
 * otherwise need. Showing every language while editing is also the more
 * useful state for comparing them, which a hidden panel would prevent.
 *
 * On the published page, this DOES hide inactive panels — see
 * `remarkAdaDirectives` in @staleless/markdown, which also remembers a
 * visitor's chosen language across every code group on the page, which is
 * why that one needs a (small, scoped) script rather than the CSS-only trick
 * the other tab blocks use.
 */
export function CodeGroupView({ node, editor, getPos }: NodeViewProps) {
  const [active, setActive] = useState(0)
  const labels: string[] = []
  node.forEach((child) => {
    const lang = (child.attrs['language'] as string | null) ?? ''
    labels.push(metaTitle(child.attrs['meta'] as string | null) ?? (lang || 'Text'))
  })
  const current = Math.min(active, Math.max(0, labels.length - 1))

  const jumpTo = (i: number) => {
    setActive(i)
    const pos = getPos?.()
    if (pos === undefined) return
    let target = pos + 1
    node.forEach((child, offset, index) => {
      if (index === i) target = pos + 1 + offset + 1
    })
    editor.chain().focus(target).scrollIntoView().run()
  }

  const addPanel = () => {
    const pos = getPos?.()
    if (pos === undefined) return
    const end = pos + node.nodeSize - 1
    editor
      .chain()
      .focus()
      .insertContentAt(end, { type: 'codeBlock', attrs: { language: null, meta: null } })
      .run()
    setActive(labels.length)
  }

  return (
    <NodeViewWrapper className="be-codegroup" data-sls="codegroup">
      <div className="be-codegroup__strip" contentEditable={false}>
        {labels.map((label, i) => (
          <button
            key={i}
            className={`be-codegroup__tab${i === current ? ' is-active' : ''}`}
            onClick={() => jumpTo(i)}
            type="button"
          >
            {label}
          </button>
        ))}
        {editor.isEditable && (
          <button
            className="be-codegroup__add"
            onClick={addPanel}
            type="button"
            data-tip="Add a language"
          >
            +
          </button>
        )}
      </div>
      <NodeViewContent className="be-codegroup__body" />
    </NodeViewWrapper>
  )
}
